import { GraphQLError } from "graphql";
import PropTypes from "prop-types";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { requestBrochureQuery } from "rsl-api-106/lib/queries";
import { Maybe, Query, QueryRequestBrochureArgs } from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import Button from "@components/_Layout/Controls/Button";
import InputLabel from "@components/_Layout/Controls/InputLabel";
import InputText from "@components/_Layout/Controls/InputText";
import SelectBooklet from "@components/_Layout/Controls/SelectBooklet";
import SelectCountry from "@components/_Layout/Controls/SelectCountry";
import { useAuth, useSession } from "@hooks";

type BookletsAreaProps = {
  //
};

const languagesVarsCodes = [
  "LANG_neobooking_booklet_demand_in_progress", //Demande de brochure en cours…
  "LANG_neobooking_booklet_request_send", //Votre demande de brochure a bien été envoyé.
  "LANG_neobooking_booklet_request", //Demande de brochure
  "LANG_neobooking_logged_as", //Connecté en tant que
  "LANG_neobooking_adr_street", //N° et voie
  "LANG_neobooking_adr_locality", //Lieu-dit / Boite postale
  "LANG_neobooking_adr_complementary_1", //Complément d'adresse 1 (app, esc)
  "LANG_neobooking_adr_complementary_2", //Complément d'adresse 2 (bat, lotissement)
  "LANG_neobooking_country", //Pays
  "LANG_neobooking_adr_zipcode", //Code postal
  "LANG_neobooking_city", //Ville
  "LANG_neobooking_booklet_ask", //Demandez
  "LANG_neobooking_area_booklets_title", //Brochures
];

const BookletsArea: React.FC<BookletsAreaProps> = () => {
  /* Hooks */
  const { user } = useAuth();
  const { sessionId, getLanguageVarValue } = useSession();
  const client = useApolloClient();

  /* State */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [booklet, setBooklet] = useState<string>("");
  const [country, setCountry] = useState<Maybe<string> | undefined>(
    user?.address?.addressCountry?.code
  );
  const [streetName, setStreetName] = useState<Maybe<string> | undefined>(
    user?.address?.streetName
  );
  const [postalLocality, setPostalLocality] = useState<
    Maybe<string> | undefined
  >(user?.address?.postalLocality);
  const [addressComplementary1, setAddressComplementary1] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary1);
  const [addressComplementary2, setAddressComplementary2] = useState<
    Maybe<string> | undefined
  >(user?.address?.addressComplementary2);
  const [zipCode, setZipCode] = useState<Maybe<string> | undefined>(
    user?.address?.zipCode
  );
  const [city, setCity] = useState<Maybe<string> | undefined>(
    user?.address?.city
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const toastBooklet = toast.loading(
      getLanguageVarValue("LANG_neobooking_booklet_demand_in_progress")
    );
    client
      .query<Query, QueryRequestBrochureArgs>({
        query: requestBrochureQuery(),
        variables: {
          session: { name: sessionId!! },
          input: {
            brochureIds: [booklet],
          },
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        if (response.data.requestBrochure?.requested) {
          toast.success(
            getLanguageVarValue("LANG_neobooking_booklet_demand_in_progress"),
            {
              id: toastBooklet,
            }
          );
        }
      })
      .catch((err: GraphQLError) => {
        console.log(err);
        toast.error(err.message, {
          id: toastBooklet,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <span className="tw-block tw-font-bold">
        {getLanguageVarValue("LANG_neobooking_booklet_request")}
      </span>
      <span className="tw-block tw-italic">
        {getLanguageVarValue("LANG_neobooking_logged_as")} {user?.firstName}{" "}
        {user?.lastName}
      </span>
      <form onSubmit={handleSubmit}>
        <div className="tw-my-6">
          <div className="tw-grid tw-grid-cols-2 tw-gap-2">
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel required>
                {getLanguageVarValue("LANG_neobooking_area_booklets_title")}
              </InputLabel>
              <SelectBooklet
                required
                value={booklet}
                onChange={(e) => setBooklet(e.target.value)}
                additionalClassname="tw-w-full"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1 tw-hidden md:tw-block"></div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel required>
                {getLanguageVarValue("LANG_neobooking_adr_street")}
              </InputLabel>
              <InputText
                required
                value={streetName!!}
                onChange={(e) => setStreetName(e.target.value)}
                placeholder=""
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel>
                {getLanguageVarValue("LANG_neobooking_adr_locality")}
              </InputLabel>
              <InputText
                placeholder=""
                value={postalLocality!!}
                onChange={(e) => setPostalLocality(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel>
                {getLanguageVarValue("LANG_neobooking_adr_complementary_1")}
              </InputLabel>
              <InputText
                placeholder=""
                value={addressComplementary1!!}
                onChange={(e) => setAddressComplementary1(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel>
                {getLanguageVarValue("LANG_neobooking_adr_complementary_2")}
              </InputLabel>
              <InputText
                placeholder=""
                value={addressComplementary2!!}
                onChange={(e) => setAddressComplementary2(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel required>
                {getLanguageVarValue("LANG_neobooking_country")}
              </InputLabel>
              <SelectCountry
                required
                value={country!!}
                onChange={(e) => setCountry(e.target.value)}
                additionalClassname="tw-w-full"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel required>
                {getLanguageVarValue("LANG_neobooking_adr_zipcode")}
              </InputLabel>
              <InputText
                required
                placeholder="06000"
                value={zipCode!!}
                onChange={(e) => setZipCode(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
            <div className="tw-col-span-2 md:tw-col-span-1">
              <InputLabel required>
                {getLanguageVarValue("LANG_neobooking_city")}
              </InputLabel>
              <InputText
                required
                placeholder={getLanguageVarValue("LANG_neobooking_city")}
                value={city!!}
                onChange={(e) => setCity(e.target.value)}
                additionalClassname="tw-w-full"
                type="text"
              />
            </div>
          </div>
          <div className="tw-my-4">
            <Button type="submit" isLoading={isLoading}>
              {getLanguageVarValue("LANG_neobooking_booklet_ask")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

BookletsArea.propTypes = {
  //
};

export default BookletsArea;
